/**
 * VendFactura Offline Database Service
 * IndexedDB-backed storage for offline POS operations
 */
import { openDB } from 'idb';

const DB_NAME = 'vendfactura-offline';
const DB_VERSION = 1;

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Products cache
        if (!db.objectStoreNames.contains('products')) {
          const productStore = db.createObjectStore('products', { keyPath: 'id' });
          productStore.createIndex('shop_id', 'shop_id');
          productStore.createIndex('category', 'category');
        }
        // Pending sales (created offline, not yet synced)
        if (!db.objectStoreNames.contains('pendingSales')) {
          const salesStore = db.createObjectStore('pendingSales', { keyPath: 'offline_id' });
          salesStore.createIndex('shop_id', 'shop_id');
          salesStore.createIndex('created_at', 'created_at');
        }
        // Shop info cache
        if (!db.objectStoreNames.contains('shops')) {
          db.createObjectStore('shops', { keyPath: 'id' });
        }
        // Sync metadata
        if (!db.objectStoreNames.contains('syncMeta')) {
          db.createObjectStore('syncMeta', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

// ==================== PRODUCTS ====================

export async function cacheProducts(shopId, products) {
  const db = await getDB();
  const tx = db.transaction('products', 'readwrite');
  // Clear old products for this shop
  const index = tx.store.index('shop_id');
  let cursor = await index.openCursor(IDBKeyRange.only(shopId));
  while (cursor) {
    await cursor.delete();
    cursor = await cursor.continue();
  }
  // Insert fresh products
  for (const product of products) {
    await tx.store.put({ ...product, shop_id: shopId });
  }
  await tx.done;
  // Update last sync time
  await updateSyncMeta('products_last_sync', Date.now());
}

export async function getCachedProducts(shopId) {
  const db = await getDB();
  const index = db.transaction('products').store.index('shop_id');
  return index.getAll(shopId);
}

// ==================== SHOPS ====================

export async function cacheShop(shop) {
  const db = await getDB();
  await db.put('shops', shop);
}

export async function getCachedShop() {
  const db = await getDB();
  const all = await db.getAll('shops');
  return all.length > 0 ? all[0] : null;
}

// ==================== PENDING SALES ====================

export async function savePendingSale(sale) {
  const db = await getDB();
  const offlineId = `offline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const pendingSale = {
    ...sale,
    offline_id: offlineId,
    synced: false,
    created_at: new Date().toISOString(),
  };
  await db.put('pendingSales', pendingSale);
  return pendingSale;
}

export async function getPendingSales(shopId) {
  const db = await getDB();
  const all = await db.getAllFromIndex('pendingSales', 'shop_id', shopId);
  return all.filter(s => !s.synced);
}

export async function getAllPendingSales() {
  const db = await getDB();
  const all = await db.getAll('pendingSales');
  return all.filter(s => !s.synced);
}

export async function markSaleSynced(offlineId) {
  const db = await getDB();
  const sale = await db.get('pendingSales', offlineId);
  if (sale) {
    sale.synced = true;
    await db.put('pendingSales', sale);
  }
}

export async function removeSyncedSales() {
  const db = await getDB();
  const tx = db.transaction('pendingSales', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    if (cursor.value.synced) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
  await tx.done;
}

export async function getPendingSalesCount() {
  const all = await getAllPendingSales();
  return all.length;
}

// ==================== SYNC META ====================

async function updateSyncMeta(key, value) {
  const db = await getDB();
  await db.put('syncMeta', { key, value });
}

export async function getSyncMeta(key) {
  const db = await getDB();
  const record = await db.get('syncMeta', key);
  return record?.value ?? null;
}

// ==================== CLEAR ====================

export async function clearAllOfflineData() {
  const db = await getDB();
  const tx1 = db.transaction('products', 'readwrite');
  await tx1.store.clear();
  await tx1.done;
  const tx2 = db.transaction('shops', 'readwrite');
  await tx2.store.clear();
  await tx2.done;
  const tx3 = db.transaction('syncMeta', 'readwrite');
  await tx3.store.clear();
  await tx3.done;
}
